if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        // Only boots need a before/after snapshot for Shopify's stock-limited partial adds.
        let bootsBefore = null;
        if (this.dataset.bootsPartialMessage) {
          try {
            const beforeResponse = await fetch(`${routes.cart_url}.js`);
            if (beforeResponse.ok) {
              const before = await beforeResponse.json();
              bootsBefore = before.items.filter(item => String(item.variant_id) === String(variantId))
                .reduce((sum, item) => sum + item.quantity, 0);
            }
          } catch { /* Native submission still proceeds if the cart read fails. */ }
        }
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              if (this.dataset.bootsPartialMessage) {
                this.handleErrorMessage(`${response.description || response.message} ${this.dataset.bootsCartUncertain}`);
              }
              if (bootsBefore !== null) {
                try {
                  const cartResponse = await fetch(`${routes.cart_url}.js`);
                  if (!cartResponse.ok) throw new Error('Cart read failed');
                  const actualCart = await cartResponse.json();
                  const actualCount = actualCart.items.filter(item => String(item.variant_id) === String(variantId))
                    .reduce((sum, item) => sum + item.quantity, 0);
                  const added = actualCount - bootsBefore;
                  if (added > 0) {
                    this.handleErrorMessage(this.dataset.bootsPartialMessage
                      .replace('[added]', String(added)).replace('[count]', String(actualCount)));
                    if (this.cart) {
                      const sectionIds = this.cart.getSectionsToRender().map(section => section.id);
                      const sectionsResponse = await fetch(`${routes.cart_url}?sections=${sectionIds.join(',')}`);
                      if (!sectionsResponse.ok) throw new Error('Cart sections read failed');
                      const sections = await sectionsResponse.json();
                      if (sectionIds.every(id => typeof sections[id] === 'string')) {
                        this.cart.renderContents({ ...actualCart, id: variantId, key: actualCart.items.find(item => String(item.variant_id) === String(variantId))?.key, sections });
                        const drawerBody = this.cart.querySelector('.drawer__inner');
                        if (drawerBody) {
                          const notice = document.createElement('p');
                          notice.className = 'tcs-boots-cart-limit';
                          notice.setAttribute('role', 'status');
                          notice.textContent = this.errorMessage.textContent;
                          drawerBody.querySelector('.drawer__header')?.after(notice);
                        }
                      }
                    }
                  }
                } catch { /* Preserve the native error and cart link if recovery reads fail. */ }
              }
              if (this.dataset.bootsPartialMessage && this.errorMessageWrapper) {
                this.errorMessageWrapper.querySelector('[data-boots-cart-review]')?.remove();
                const link = document.createElement('a');
                link.href = routes.cart_url;
                link.dataset.bootsCartReview = '';
                link.textContent = this.dataset.bootsCartLabel;
                this.errorMessageWrapper.append(link);
              }
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (!errorMessage) this.errorMessageWrapper.querySelector('[data-boots-cart-review]')?.remove();

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
